import { useCallback, useMemo, type MouseEvent as ReactMouseEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import type {
  ListRiskScoresCommandOutput,
  RiskScore,
  RiskScoreWindow,
} from '@amzn/gbsa-helios-typescript-client';
import type { UseQueryResult } from '@tanstack/react-query';
import {
  ServerSideDataTable,
  type TableColumnDefinition,
} from '@amzn/gsce-shared-react-lib/components/table';
import { Link } from '@amzn/stencil-react-components/link';
import { SortDirection, TableSpacing } from '@amzn/stencil-react-components/table';

import type { Messages } from 'src/v2/shared/i18n/useMessages';
import { ROUTE_RISK_ENGINE_DETAIL } from 'src/v2/routes';
import {
  RISK_TABLE_SKELETON_ROW_COUNT,
  formatRiskGap,
  formatRiskValue,
} from '../logic/riskScorePresentation';
import type { RiskEngineMessageKey } from '../translations';
import { RISK_ENGINE_TEST_IDS } from '../testIds';
import { RiskStatusIndicator } from './RiskStatusIndicator';

type RiskEngineTranslator = Messages<RiskEngineMessageKey>['t'];

interface RiskScoreRow extends RiskScore {
  readonly rowId: string;
}

interface RiskMetricsTableProps {
  /** The current page's query result; drives rows, total count, loading and error. */
  readonly query: UseQueryResult<ListRiskScoresCommandOutput>;
  readonly ariaLabel: string;
  readonly t: RiskEngineTranslator;
  /** 1-based page index (controlled by the page). */
  readonly currentPage: number;
  readonly pageSize: number;
  readonly onPageChange: (page: number) => void;
  /** Id of the currently sorted column (Stencil's sort shape). */
  readonly activeSortId: string | undefined;
  readonly sortDirection: SortDirection;
  /** Fired when a sortable header is clicked; the page maps it to the request's sort key. */
  readonly onSort: (state: { sortId: string; sortDirection: SortDirection }) => void;
  /** The list's active scoring window, carried into each row's breakdown link. */
  readonly activeTimeWindow: RiskScoreWindow;
}

const RISK_ENGINE_TABLE_ID = 'risk-engine-metrics';

/** Number of columns in the risk scores table; drives the uniform column width. */
export const RISK_TABLE_COLUMN_COUNT = 7;

/**
 * Shared layout for every risk scores column: equal widths and centered content (header and
 * cell). Spread into each column definition so the layout stays uniform when columns change.
 */
const RISK_COLUMN_LAYOUT = {
  width: `${100 / RISK_TABLE_COLUMN_COUNT}%`,
  textAlign: 'center',
} as const satisfies Pick<TableColumnDefinition<RiskScoreRow>, 'width' | 'textAlign'>;

function riskScoreKey(score: RiskScore): string {
  return `${score.location}:${score.metric}:${score.metricGroupId}`;
}

/** Maps the server page into table rows with a stable composite id. */
function getItems(data: ListRiskScoresCommandOutput): RiskScoreRow[] {
  return data.riskScores.map((score) => ({ ...score, rowId: riskScoreKey(score) }));
}

/** Grand total across all pages, for the pager's page count. */
function getTotalItems(data: ListRiskScoresCommandOutput): number {
  return data.totalCount ?? 0;
}

/**
 * The drill-down URL for a root row: its station and metric group identify the group to open, and
 * the active scoring window is carried through so the breakdown opens in the same window the list
 * is showing.
 */
function buildBreakdownHref(
  location: string,
  metricGroupId: string,
  timeWindow: RiskScoreWindow,
): string {
  const params = new URLSearchParams({ location, metricGroupId, timeWindow });
  return `${ROUTE_RISK_ENGINE_DETAIL}?${params.toString()}`;
}

/**
 * The metric-name cell as a link into the per-station impact breakdown. A real anchor (so it is
 * keyboard-reachable, middle-clickable and deep-link testable) whose primary-button click is
 * intercepted for SPA navigation; modified clicks fall through to the browser so "open in new tab"
 * still works.
 */
function MetricBreakdownLink({
  score,
  timeWindow,
}: {
  readonly score: RiskScoreRow;
  readonly timeWindow: RiskScoreWindow;
}): JSX.Element {
  const navigate = useNavigate();
  const label = score.displayName ?? score.metric;
  const href = buildBreakdownHref(score.location, score.metricGroupId, timeWindow);
  const handleClick = useCallback(
    (event: ReactMouseEvent<HTMLAnchorElement>) => {
      if (
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      ) {
        return;
      }
      event.preventDefault();
      void navigate(href);
    },
    [navigate, href],
  );
  return (
    <span
      data-testid={RISK_ENGINE_TEST_IDS.scoreRow(score.location, score.metricGroupId)}
    >
      <Link href={href} onClick={handleClick}>
        {label}
      </Link>
    </span>
  );
}

/**
 * Risk-specific column definitions and formatting over the shared server-paginated table.
 * Fetches exactly one page at a time via `query`. Category and metric name come straight from the
 * backend (`category`, `displayName`) — no client-side metric→category mapping.
 */
export function RiskMetricsTable({
  query,
  ariaLabel,
  t,
  currentPage,
  pageSize,
  onPageChange,
  activeSortId,
  sortDirection,
  onSort,
  activeTimeWindow,
}: RiskMetricsTableProps): JSX.Element {
  const headingLocation = t('columnLocation');
  const headingCategory = t('columnMetricCategory');
  const headingMetric = t('columnMetric');
  const headingStatus = t('columnRiskStatus');
  const headingActual = t('columnActual');
  const headingTarget = t('columnTarget');
  const headingGap = t('columnGapToTarget');

  const columns = useMemo<TableColumnDefinition<RiskScoreRow>[]>(
    () => [
      {
        ...RISK_COLUMN_LAYOUT,
        id: 'location',
        header: headingLocation,
        cell: (score: RiskScoreRow) => score.location,
      },
      {
        ...RISK_COLUMN_LAYOUT,
        id: 'category',
        header: headingCategory,
        cell: (score: RiskScoreRow) => score.category ?? '—',
        sortable: true,
      },
      {
        ...RISK_COLUMN_LAYOUT,
        id: 'metric',
        header: headingMetric,
        cell: (score: RiskScoreRow) => (
          <MetricBreakdownLink score={score} timeWindow={activeTimeWindow} />
        ),
        sortable: true,
      },
      {
        ...RISK_COLUMN_LAYOUT,
        id: 'status',
        header: headingStatus,
        cell: (score: RiskScoreRow) => (
          <RiskStatusIndicator status={score.status} t={t} centered />
        ),
        sortable: true,
      },
      {
        ...RISK_COLUMN_LAYOUT,
        id: 'actual',
        header: headingActual,
        cell: (score: RiskScoreRow) =>
          formatRiskValue(score.actualValue, score.metricUnit),
      },
      {
        ...RISK_COLUMN_LAYOUT,
        id: 'target',
        header: headingTarget,
        cell: (score: RiskScoreRow) =>
          formatRiskValue(score.targetValue, score.metricUnit),
      },
      {
        ...RISK_COLUMN_LAYOUT,
        id: 'gap',
        header: headingGap,
        cell: (score: RiskScoreRow) => formatRiskGap(score.gapToTargetPct),
        sortable: true,
      },
    ],
    [
      t,
      activeTimeWindow,
      headingActual,
      headingCategory,
      headingGap,
      headingLocation,
      headingMetric,
      headingStatus,
      headingTarget,
    ],
  );

  return (
    <div data-testid={RISK_ENGINE_TEST_IDS.desktopTable}>
      <ServerSideDataTable
        tableId={RISK_ENGINE_TABLE_ID}
        query={query}
        getItems={getItems}
        getTotalItems={getTotalItems}
        columnDefinitions={columns}
        trackBy="rowId"
        aria-label={ariaLabel}
        fullWidth
        shouldScrollHorizontally={false}
        showSettings={false}
        skeletonRowCount={RISK_TABLE_SKELETON_ROW_COUNT}
        spacing={TableSpacing.Reduced}
        currentPage={currentPage}
        pageSize={pageSize}
        onPageChange={onPageChange}
        showRefresh={false}
        {...(activeSortId !== undefined ? { activeSortId } : {})}
        sortDirection={sortDirection}
        onSort={onSort}
      />
    </div>
  );
}
